import { type ReactNode, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  PAYMENT_STATUSES,
  type Locale,
  type PaymentMethod,
  type PaymentQueueItem,
  type PaymentStatus,
} from '@aps/contracts';
import {
  Button,
  DataTable,
  type DataTableColumn,
  EmptyState,
  Skeleton,
  formatMoney,
} from '@aps/ui';
import { ApiError, api } from '../lib/api.js';
import { messages, waitingText } from '../i18n/messages.js';
import { useSession } from '../app/session-context.js';
import { QueueError } from './WorkQueueScreen.js';

interface PaymentsScreenProps {
  onOpenOrder: (orderId: string) => void;
}

const PAGE_SIZE = 25;

/**
 * Usul qatorda KO'RSATILADI: aynan u tasdiqlash uchun nima kerakligini
 * belgilaydi — naqddan boshqa usulda bank ma'lumotnomasi majburiy
 * (`requiresBankRef`). Usulsiz ro'yxat tasdiqlovchiga "buni hozir
 * yopa olamanmi" degan savolga javob bermaydi.
 */
const METHOD_LABELS: Record<PaymentMethod, Record<Locale, string>> = {
  CASH: { UZ: 'Naqd', RU: 'Наличные' },
  CARD_TRANSFER: { UZ: "Kartaga o'tkazma", RU: 'Перевод на карту' },
  PAYME: { UZ: 'Payme', RU: 'Payme' },
  CLICK: { UZ: 'Click', RU: 'Click' },
};

const STATUS_LABELS: Record<PaymentStatus, { UZ: string; RU: string }> = {
  PENDING: { UZ: 'Tasdiqlanmagan', RU: 'Не подтверждён' },
  CONFIRMED: { UZ: 'Tasdiqlangan', RU: 'Подтверждён' },
  REJECTED: { UZ: 'Rad etilgan', RU: 'Отклонён' },
  REFUNDED: { UZ: 'Qaytarilgan', RU: 'Возвращён' },
};

/**
 * To'lovlarni tasdiqlash navbati.
 *
 * Vazifalar ajratilishi (docs/02-MAHSULOT.md): pul olinganini Operator
 * yozadi, tasdiqlashni Admin qiladi. Demak yozilgan qator kimdir uni
 * KO'RGUNCHA osilib turadi — bu ekran o'sha ko'rish joyi. Usiz
 * tasdiqlovchi qatorni faqat buyurtmani ochib tasodifan topardi.
 *
 * Bu ish navbatidagi `CHECK_PAYMENT` guruhidan BOSHQA narsa: u
 * jo'natma statusi bo'yicha yig'iladi va hali hech kim pul yozmagan
 * buyurtmalarni ko'rsatadi.
 */
export function PaymentsScreen({ onOpenOrder }: PaymentsScreenProps): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);

  const [status, setStatus] = useState<PaymentStatus>('PENDING');
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors.at(-1);

  const page = useQuery({
    queryKey: ['payment-queue', status, cursor ?? null],
    queryFn: () =>
      api.paymentQueue({
        status,
        limit: PAGE_SIZE,
        ...(cursor === undefined ? {} : { cursor }),
      }),
    placeholderData: keepPreviousData,
  });

  const header = (
    <header className="adm-page__header">
      <h1 className="adm-page__title">{t.navPayments}</h1>
    </header>
  );

  const filters = (
    <div className="adm-filters">
      <div className="adm-filters__chips">
        {PAYMENT_STATUSES.map((value) => (
          <Button
            key={value}
            variant={status === value ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => {
              setStatus(value);
              setCursors([]);
            }}
          >
            {STATUS_LABELS[value][locale]}
          </Button>
        ))}
      </div>
    </div>
  );

  if (page.isPending) {
    return (
      <section className="adm-page">
        {header}
        {filters}
        <Skeleton height="220px" label={t.loading} />
      </section>
    );
  }

  if (page.isError) {
    return (
      <section className="adm-page">
        {header}
        {filters}
        <QueueError
          message={page.error instanceof ApiError ? page.error.text(locale) : t.errorTitle}
          retryLabel={t.retry}
          onRetry={() => void page.refetch()}
        />
      </section>
    );
  }

  const now = Date.now();

  const columns: DataTableColumn<PaymentQueueItem>[] = [
    {
      key: 'order',
      header: t.colOrder,
      render: (row) => (
        <button
          className="adm-link"
          type="button"
          onClick={() => {
            onOpenOrder(row.payment.orderId);
          }}
        >
          {row.orderNumber}
        </button>
      ),
    },
    {
      key: 'customer',
      header: t.colCustomer,
      render: (row) => (
        <span>
          {row.customerName}
          <br />
          <span className="aps-muted">{row.customerPhone}</span>
        </span>
      ),
    },
    {
      key: 'amount',
      header: t.paymentAmount,
      numeric: true,
      render: (row) => formatMoney(row.payment.amount, locale),
    },
    {
      key: 'method',
      header: t.paymentMethod,
      render: (row) => METHOD_LABELS[row.payment.method][locale],
    },
    {
      /*
       * Qator summasi o'zi yetarli emas: 200 000 so'm — bu to'liq
       * to'lovmi yoki qismanmi, buyurtma summasi va allaqachon
       * tasdiqlangani bilan solishtirgandagina ma'lum bo'ladi.
       */
      key: 'order-total',
      header: t.paymentOfTotal,
      numeric: true,
      render: (row) => (
        <span>
          {formatMoney(row.paidTotal, locale)}
          <br />
          <span className="aps-muted">/ {formatMoney(row.grandTotal, locale)}</span>
        </span>
      ),
    },
    {
      /*
       * Mijoz botdan chek yuborsa qator SHU BELGI bilan farqlanadi.
       * Usiz operator bir xil summali ikki qatorni ajrata olmasdi.
       */
      key: 'receipt',
      header: t.paymentReceipt,
      render: (row) =>
        row.payment.receiptUrl === null ? (
          <span className="aps-muted">—</span>
        ) : (
          <span className="adm-badge adm-badge--pending">{t.paymentHasReceipt}</span>
        ),
    },
    {
      key: 'waiting',
      header: t.colWaiting,
      render: (row) => waitingText(row.payment.createdAt, now, locale),
    },
  ];

  return (
    <section className="adm-page">
      {header}
      {filters}

      <DataTable
        caption={t.navPayments}
        columns={columns}
        rows={page.data.items}
        rowKey={(row) => row.payment.id}
        empty={<EmptyState title={t.paymentQueueEmpty} hint={t.paymentQueueEmptyHint} />}
      />

      {page.data.nextCursor !== null && (
        <div className="adm-table-footer">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              const next = page.data.nextCursor;
              if (next !== null) setCursors((current) => [...current, next]);
            }}
          >
            {t.loadMore}
          </Button>
        </div>
      )}
    </section>
  );
}
